import { passwordRequirementTooltip } from '@/components';
import { AUTH_CHALLENGE } from '@/enums';
import { useParseQueryString } from '@/hooks';
import { PUBLIC_ROUTES } from '@/routes';
import { AuthService } from '@/services';
import { CustomError, errorNotification } from '@/utils';
import {
  INVALID_FIELDS,
  passwordPatternChecking,
  YupString,
} from '@/validations';
import { ControlledInput, CustomButton } from '@amili-v3/amili-v3-design';
import { notification } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import AuthCardHeader from '../AuthCardHeader';
import { authPasswordToggle } from '../authPasswordToggle';
import { AuthModal } from '../styles';

const validationSchema = Yup.object().shape({
  oldPassword: YupString,
  password: YupString.matches(
    passwordPatternChecking,
    INVALID_FIELDS.PASSWORD_PATTERN,
  ),
  newPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Not match')
    .required(INVALID_FIELDS.REQUIRED),
});

const PasswordComplexity = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  const { accessToken, challengeName } = useParseQueryString<{
    accessToken: string;
    challengeName: string;
  }>();

  const isPasswordComplexity =
    challengeName === AUTH_CHALLENGE.PASSWORD_COMPLEXITY;

  const mutationChangePassword = useMutation({
    mutationFn: AuthService.changePassword,
    onSuccess: () => {
      notification.success({ message: t('common.success') });
      setOpen(false);
      setTimeout(() => {
        navigate(PUBLIC_ROUTES.SIGN_IN);
      }, 200);
    },
    onError: error =>
      errorNotification({ error: error as unknown as CustomError }),
  });

  const handleChangePassword = (values: any) => {
    return mutationChangePassword.mutateAsync({
      ...values,
      previousPassword: values?.oldPassword ?? '',
      proposedPassword: values?.newPassword ?? '',
      accessToken,
    });
  };

  const title = t(
    `authentication.${
      isPasswordComplexity
        ? 'forceResetWeakPassword'
        : 'forceResetExpiredPassword'
    }`,
  );

  return (
    <AuthModal
      centered
      open={open}
      mask={false}
      width={420}
      hideCloseButton
      layout="vertical"
      footerBorder={false}
      title={<AuthCardHeader heading={title} />}
      goBack={() => {
        setOpen(false);
        setTimeout(() => {
          navigate(PUBLIC_ROUTES.SIGN_IN);
        }, 200);
      }}
      formikProps={{
        validationSchema,
        onSubmit: handleChangePassword,
        initialValues: { password: '', oldPassword: '', newPassword: '' },
      }}
      footer={[
        <CustomButton
          size="large"
          width="100%"
          key="submit"
          form="modal-id"
          htmlType="submit"
          aria-label="Submit btn"
          loading={mutationChangePassword.isPending}
        >
          {t('common.submit')}
        </CustomButton>,
      ]}
    >
      <ControlledInput
        required
        name="oldPassword"
        inputType="password"
        passwordIconRender={authPasswordToggle}
        label={t('authentication.currentPassword')}
      />
      <ControlledInput
        required
        name="password"
        inputType="password"
        passwordIconRender={authPasswordToggle}
        label={t('common.password')}
        customFormProps={{ tooltip: passwordRequirementTooltip }}
      />
      <ControlledInput
        required
        name="newPassword"
        inputType="password"
        passwordIconRender={authPasswordToggle}
        label={t('authentication.confirmPassword')}
      />
    </AuthModal>
  );
};

export default PasswordComplexity;
